import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  List,
  ThemeIcon,
  Grid,
  Alert,
  Image,
  Flex,
} from '@mantine/core';
import { IconCheck, IconBrandInstagram } from '@tabler/icons-react';

// --- Contact form temporarily hidden (see commented JSX + logic below). ---
// --- To re-enable, uncomment this block and the form logic/JSX further down. ---
// import { useState } from 'react';
// import {
//   Paper,
//   TextInput,
//   Textarea,
//   Button,
//   Group,
//   LoadingOverlay,
// } from '@mantine/core';
// import { useForm } from '@mantine/form';
// import { notifications } from '@mantine/notifications';
// import { submitContactForm } from '../services/api';

export default function Contact() {
  // --- Contact form logic temporarily disabled (form is hidden). ---
  // --- Re-enable together with the imports above and the JSX below. ---
  /*
  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      message: '',
    },
    validate: {
      firstName: (value) => (value.length < 2 ? 'First name must have at least 2 letters' : null),
      lastName: (value) => (value.length < 2 ? 'Last name must have at least 2 letters' : null),
      email: (value) => {
        // Match backend validation: require @ and . with characters between
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value.trim()) ? null : 'Please enter a valid email address (e.g., name@domain.com)';
      },
      phone: (value) => {
        // Optional field - only validate if provided
        if (!value || value.trim() === '') return null;

        // Allow digits, spaces, hyphens, parentheses, and plus sign
        // Minimum 10 digits for a valid phone number
        const phoneRegex = /^[\d\s\-\(\)\+\.]+$/;
        const digitCount = value.replace(/\D/g, '').length;

        if (!phoneRegex.test(value)) {
          return 'Phone number can only contain digits, spaces, hyphens, parentheses, and plus sign';
        }
        if (digitCount < 10) {
          return 'Phone number must contain at least 10 digits';
        }
        if (digitCount > 15) {
          return 'Phone number cannot exceed 15 digits';
        }
        return null;
      },
      message: (value) => (value.length < 10 ? 'Message must be at least 10 characters' : null),
    },
  });

  // Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  //
   // Handles contact form submission
   // Validates form, calls API, displays notifications
   //
  const handleSubmit = async (values: typeof form.values) => {
    // Prevent multiple submissions
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Call backend API
      const response = await submitContactForm(values);

      if (response.success) {
        // Show success notification
        notifications.show({
          title: 'Success!',
          message: response.message || 'Your inquiry has been submitted successfully. We will contact you shortly.',
          color: 'green',
          autoClose: 5000,
          withCloseButton: true,
        });

        // Reset form to initial state
        form.reset();
      } else {
        // Show error notification with details
        const errorMessage = response.errors
          ? response.errors.join(', ')
          : response.message || 'Please check your input and try again.';

        notifications.show({
          title: 'Submission Failed',
          message: errorMessage,
          color: 'red',
          autoClose: 7000,
          withCloseButton: true,
        });
      }
    } catch (error) {
      // Show generic error notification for unexpected errors
      console.error('Unexpected error during form submission:', error);
      notifications.show({
        title: 'Error',
        message: 'An unexpected error occurred. Please try again later.',
        color: 'red',
        autoClose: 5000,
        withCloseButton: true,
      });
    } finally {
      // Always clear loading state
      setIsSubmitting(false);
    }
  };
  */

  return (
    <Box mih="100vh" bg="gray.1">
      {/* Hero Section */}
      <Box
        c="white"
        py="sm"
        px="sm"
        pos="relative"
        style={{
          overflow: 'hidden',
          backgroundImage: 'url(/header-bg-2.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Container size="lg">
          <Stack gap="md" maw={800}>
            <Flex align="center" gap="lg">
              <Image
                src="/logo_A_256.png"
                alt="Carson Capital Management Logo"
                w={120}
                h={120}
                fit="contain"
              />
              <Box>
                <Title order={1} size="2rem" fw={700} lh={1.2}>
                  Carson Capital Management
                </Title>
                <Text size="lg" fw={500} mt="xs" opacity={0.95}>
                  Strategic Capital for Disciplined Growth
                </Text>
              </Box>
            </Flex>
            <Box px="xl">
              <Title order={2} size="1.2rem" fw={400} c="accent-orange">
                Your Comprehensive Financial Advisory & Capital Management Partner
              </Title>
              <Text size="md" c="gray.4" lh={1.6} mt="xs">
                A private partnership focused on delivering transparent strategies and disciplined
                performance oversight. We help investors and businesses structure, manage, and grow
                their capital through tailored financial solutions.
              </Text>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container size="lg" py={48}>
        <Grid gutter="xl" align="flex-start">
          {/* Left Column - Services */}
          <Grid.Col span={12}>
            <Title order={3} size="1.5rem" mb="xl" c="primary-blue">
              Our Services
            </Title>
            <List
              spacing="md"
              size="md"
              icon={
                <ThemeIcon color="accent-green" size={24} radius="xl">
                  <IconCheck size={16} />
                </ThemeIcon>
              }
            >
              <List.Item>
                <Text fw={500}>Financial Advisory Services</Text>
                <Text size="sm" c="dimmed">Strategic guidance for long-term financial success</Text>
              </List.Item>
              <List.Item>
                <Text fw={500}>Investment Planning</Text>
                <Text size="sm" c="dimmed">Customized investment strategies aligned with your goals</Text>
              </List.Item>
              <List.Item>
                <Text fw={500}>Capital Management Consulting</Text>
                <Text size="sm" c="dimmed">Expert oversight and performance monitoring</Text>
              </List.Item>
              <List.Item>
                <Text fw={500}>Portfolio Structuring</Text>
                <Text size="sm" c="dimmed">Disciplined approach to asset allocation and risk management</Text>
              </List.Item>
              <List.Item>
                <Text fw={500}>Compliance & Regulatory Support</Text>
                <Text size="sm" c="dimmed">Maintaining highest standards of professionalism</Text>
              </List.Item>
            </List>

            <Alert mt="xl" color="blue" variant="light" radius="md">
              <Text size="sm" c="dimmed" mb="xs">Our Commitment</Text>
              <Text fw={500} c="blue.9">
                Guided by integrity, precision, and a commitment to long-term value, Carson CM
                delivers tailored financial solutions that adapt to each client's goals.
              </Text>
            </Alert>
          </Grid.Col>

          {/* Right Column - Contact Form (temporarily hidden) */}
          {/*
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow="md" p="xl" radius="md" bg="white">
              <Title order={3} size="1.5rem" mb="md" c="primary-blue">
                Contact Us
              </Title>
              <Text size="sm" c="dimmed" mb="xl">
                Get in touch to learn more about our services and how we can help you achieve
                your financial goals.
              </Text>

              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Box pos="relative">
                  <LoadingOverlay
                    visible={isSubmitting}
                    zIndex={1000}
                    overlayProps={{ radius: "sm", blur: 2 }}
                    loaderProps={{ color: 'blue', type: 'dots' }}
                  />

                  <Stack gap="md">
                    <Group grow>
                      <TextInput
                        label="First Name"
                        placeholder="John"
                        required
                        {...form.getInputProps('firstName')}
                      />
                      <TextInput
                        label="Last Name"
                        placeholder="Doe"
                        required
                        {...form.getInputProps('lastName')}
                      />
                    </Group>

                    <TextInput
                      label="Email"
                      placeholder="john.doe@example.com"
                      required
                      type="email"
                      {...form.getInputProps('email')}
                    />

                    <TextInput
                      label="Phone"
                      placeholder="+1 (555) 123-4567"
                      {...form.getInputProps('phone')}
                    />

                    <TextInput
                      label="Company"
                      placeholder="Your company name"
                      {...form.getInputProps('company')}
                    />

                    <Textarea
                      label="Message"
                      placeholder="Tell us about your needs..."
                      required
                      minRows={4}
                      {...form.getInputProps('message')}
                    />

                    <Button
                      type="submit"
                      size="md"
                      fullWidth
                      mt="md"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                    </Button>

                    <Text size="xs" c="dimmed" ta="center" mt="xs">
                      By submitting this form, you agree to our privacy policy and consent to be
                      contacted regarding our services.
                    </Text>
                  </Stack>
                </Box>
              </form>
            </Paper>
          </Grid.Col>
          */}
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        c="white"
        mt="auto"
        pos="relative"
        style={{
          height: '250px',
          backgroundImage: 'url(/footer-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container size="lg">
          <Stack gap="md" align="center">
            <a
              href="https://www.instagram.com/carson.cap?igsh=MjBtdWRiMGVha2M4"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'white',
                transition: 'opacity 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <IconBrandInstagram size={28} stroke={1.5} />
              <Text size="md" fw={500}>Follow Us</Text>
            </a>
            <Text size="sm" ta="center" opacity={0.8}>
              &copy; {new Date().getFullYear()} Carson Capital Management. All rights reserved.
            </Text>
            <Text size="xs" ta="center" opacity={0.6}>
              Investment advisory services provided by Carson Capital Management, a private partnership.
            </Text>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
